import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AppConfig } from '@app/app.config';
import { catchError, Observable, throwError, takeUntil, EMPTY, retry, timer } from 'rxjs';
import { QuotasService } from '../services/quotas.service';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class ApiInterceptor implements HttpInterceptor {
    dialogRef: any; // MatDialogRef<QuotasComponent> | undefined;
    private isToastServiceEnabled: boolean = true;
    private isRedirectingToLogin = false;

    // The BFF answers 503 while an in-flight token refresh resolves (session is still valid).
    // Silently retry these with backoff so the user never sees a transient blip; 401 is NOT retried
    // (that means logged out -> redirect to login).
    private static readonly RETRY_503_MAX_ATTEMPTS = 3;
    private static readonly RETRY_503_BASE_DELAY_MS = 400;

    constructor(
        private appConfig: AppConfig,
        private quotasService: QuotasService,
        private toastService: ToastService,
        private sessionService: SessionService,
        private router: Router,
        private readonly injector: Injector
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // Replace URL prefixes with actual API URLs
        request = this.replaceUrlPrefixes(request);

        // Handle self-service routes with session ID
        const isOnSelfservice = this.isOnSelfserviceRoute();
        const hasPermanentSessionId =
            !!localStorage.getItem('sessionIdPermanent');

        if (isOnSelfservice || hasPermanentSessionId) {
            return this.handleSelfServiceRequest(request, next);
        }

        // Ensure DLS_SESSION_ID cookie is sent with cross-origin API requests
        // Attach CSRF token from cookie as header for BFF validation
        const csrfToken = this.getCookie('DLS_BFF_CSRF_TOKEN');
        request = request.clone({
            withCredentials: true,
            ...(csrfToken && {
                headers: request.headers.set('x-csrf-token', csrfToken),
            }),
        });
        return this.handleRequest(request, next);
    }

    /**
     * Handles request with abort signal support and error handling.
     */
    private handleRequest(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<any> {
        // Check if request has an abort signal in context
        const abortSignal = request.context.get(ABORT_SIGNAL);

        // If abort signal is already aborted, don't make the request
        if (abortSignal?.aborted) {
            return EMPTY;
        }

        let requestObservable = next.handle(request);

        // If we have an abort signal, make the request cancellable
        if (abortSignal) {
            // Create abort observable - check both if already aborted and listen for future aborts
            const abort$ = new Observable(subscriber => {
                if (abortSignal.aborted) {
                    subscriber.next(true);
                    subscriber.complete();
                    return () => {}; // Return empty cleanup function
                }

                const onAbort = () => {
                    subscriber.next(true);
                    subscriber.complete();
                };

                abortSignal.addEventListener('abort', onAbort);

                return () => {
                    abortSignal.removeEventListener('abort', onAbort);
                };
            });

            requestObservable = requestObservable.pipe(
                takeUntil(abort$),
                catchError(error => {
                    if (abortSignal.aborted) {
                        return EMPTY;
                    }
                    throw error;
                })
            );
        }

        return requestObservable.pipe(
            // The BFF returns 503 (session intact) while a token refresh is in flight — retry silently
            // before falling through to error handling. The 503 is emitted before the request reaches
            // the API, so retrying is safe even for mutating calls.
            retry({
                count: ApiInterceptor.RETRY_503_MAX_ATTEMPTS,
                delay: (error, retryCount) => {
                    if (
                        error instanceof HttpErrorResponse &&
                        error.status === 503
                    ) {
                        return timer(
                            ApiInterceptor.RETRY_503_BASE_DELAY_MS * retryCount
                        );
                    }
                    return throwError(() => error);
                },
            }),
            catchError((httpEvent: HttpErrorResponse) => {
                if (
                    httpEvent.status === 403 &&
                    (httpEvent.error?.errorMessage?.includes(
                        'The terms of service have not been accepted'
                    ) ||
                        httpEvent.error?.[0]?.message?.includes(
                            'The terms of service have not been accepted'
                        ))
                ) {
                    this.router.navigate(['/settings/tos']);
                    return EMPTY;
                }
                if (
                    httpEvent.status === 409 &&
                    (httpEvent.error?.errorMessage?.includes('checkin') ||
                        request.url.includes('/tours'))
                ) {
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 404 &&
                    httpEvent.error?.errorMessage ===
                        'Self checkin config not found.'
                ) {
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 404 &&
                    httpEvent.error?.errorMessage === 'Checkout not found.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant('GLOBAL.ERROR.UNSPECIFIED')
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 409 &&
                    httpEvent.error?.errorMessage ===
                        'Delivery note ist not editable any more.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant(
                            'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-CONFLICT-ERROR'
                        )
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    (httpEvent.status === 403 &&
                        httpEvent.error?.errorMessage ===
                            'Delivery note not accessible.') ||
                    httpEvent.error?.errorMessage ===
                        'Delivery note bundle not found.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant('GLOBAL.ERROR.UNSPECIFIED')
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 400 &&
                    httpEvent.error?.errorMessage ===
                        'PDF should not be password protected.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant(
                            'ERROR.PDF_SHOULD_NOT_PASSWORD_PROTECTED'
                        )
                    );
                    return throwError(() => httpEvent);
                }
                if (
                    httpEvent.status === 400 &&
                    httpEvent.error?.errorMessage === 'PDF is not valid.'
                ) {
                    const translateService =
                        this.injector.get(TranslateService);
                    this.showToast(
                        translateService.instant('ERROR.PDF_NOT_VALID')
                    );
                    return throwError(() => httpEvent);
                }
                if (httpEvent.status === 401) {
                    if (!this.isRedirectingToLogin) {
                        this.isRedirectingToLogin = true;
                        this.router.navigate(['/login']).finally(() => {
                            this.isRedirectingToLogin = false;
                        });
                    }
                    return EMPTY;
                }
                if (httpEvent.headers.has('x-request-id')) {
                    const requestId = httpEvent.headers.get('x-request-id');

                    if (
                        requestId &&
                        !httpEvent.url?.includes('/me') &&
                        httpEvent.status !== 404
                    ) {
                        const translateService =
                            this.injector.get(TranslateService);
                        this.showToast(
                            requestId,
                            translateService.instant('ERROR.REQUEST-ID')
                        );
                    }
                }

                try {
                    if (
                        !httpEvent?.error?.errorMessage
                            ?.toLowerCase()
                            .includes('2FA') &&
                        (httpEvent?.error?.errorMessage
                            ?.toLowerCase()
                            .includes('quotas') ||
                            httpEvent?.error?.errorMessage
                                ?.toLowerCase()
                                .includes('reached') ||
                            (typeof httpEvent.error === 'string' &&
                                !httpEvent.error
                                    ?.toLowerCase()
                                    ?.includes('2FA') &&
                                (httpEvent.error
                                    ?.toLowerCase()
                                    ?.includes('quotas') ||
                                    httpEvent.error
                                        ?.toLowerCase()
                                        ?.includes('reached'))))
                    ) {
                        this.quotasService.showQuotasDialog();
                    }
                } catch (error) {}
                return throwError(() => httpEvent);
            })
        );
    }

    /**
     * Replaces URL prefixes with actual API URLs.
     */
    private replaceUrlPrefixes(request: HttpRequest<any>): HttpRequest<any> {
        const urlMappings = [
            { prefix: 'apiURL', url: this.appConfig.apiUrl },
            { prefix: 'apiV3Url', url: this.appConfig.apiV3Url },
            { prefix: 'bffUrl', url: this.appConfig.bffUrl },
        ];

        for (const mapping of urlMappings) {
            if (request.url.startsWith(mapping.prefix)) {
                const requestUrl = request.url.replace(
                    `${mapping.prefix}/`,
                    ''
                );
                return request.clone({
                    url: `${mapping.url}/${requestUrl}`,
                });
            }
        }

        return request;
    }

    /**
     * Handles self-service requests with session ID.
     */
    private handleSelfServiceRequest(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<any> {
        const sessionId = this.sessionService.getSessionId();

        if (sessionId) {
            const headers = request.headers.set('dlssessionid', sessionId);
            const authReq = request.clone({
                headers,
                withCredentials: false,
            });
            return next.handle(authReq);
        }

        return next.handle(request);
    }

    private isOnSelfserviceRoute(): boolean {
        // Check router URL
        const routerUrl = this.router.url;
        if (routerUrl && routerUrl.startsWith('/selfservice')) {
            return true;
        }

        // Also check if we have sessionIdPermanent in storage (indicates selfservice session)
        const sessionIdPermanent = localStorage.getItem('sessionIdPermanent');
        return !!sessionIdPermanent;
    }

    public disableToastService() {
        this.isToastServiceEnabled = false;
    }

    public enableToastService() {
        this.isToastServiceEnabled = true;
    }

    private getCookie(name: string): string | null {
        const match = document.cookie.match(
            new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
        );
        return match ? decodeURIComponent(match[1]) : null;
    }

    private showToast(message: string, title?: string) {
        if (this.isToastServiceEnabled) {
            this.toastService.error(message, title);
        }
    }
}
